import { ViewController, createURL, createModalNotReadyError, TronicPayloadMethod } from 'tronic-provider';
import { applyStylesToElement, IFRAME_STYLES, injectCSS, styleObjectToCSS } from './utils/css-in-js';

export interface IframeControllerConfig {
  enableUrlListening?: boolean;

  urlMapper?: (parentUrl: string) => string | null;
}

export class IframeController extends ViewController {
  public iframe: HTMLIFrameElement | null = null;
  public container: HTMLDivElement | null = null;

  private isVisible = false;
  private isMobile = false;

  private config: IframeControllerConfig;
  private urlListener: ((event: PopStateEvent) => void) | null = null;
  private lastKnownUrl = '';

  // Resource tracking for proper cleanup
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private resizeListener: (() => void) | null = null;
  private loadListener: (() => void) | null = null;
  private originalPushState: typeof History.prototype.pushState | null = null;
  private originalReplaceState: typeof History.prototype.replaceState | null = null;
  private isDestroyed = false;

  constructor(endpoint: string, parameters: string) {
    super(endpoint, parameters);
    this.config = {
      enableUrlListening: false,
    };
    this.init();
  }

  /**
   * Configure iframe controller options after construction
   * Must be called before the iframe is ready for optimal results
   */
  public configure(config: IframeControllerConfig): void {
    this.config = {
      ...this.config,
      ...config,
    };

    // If URL listening was enabled and not already set up, set it up now
    if (config.enableUrlListening && !this.urlListener) {
      this.setupUrlListener();
    }

    // If URL listening was disabled, clean up existing listener
    if (config.enableUrlListening === false && this.urlListener) {
      this.cleanupUrlListener();
    }
  }

  public init() {
    if (this.isDestroyed) {
      console.warn('⚠️ [IframeController] Cannot initialize destroyed instance');
      return;
    }

    const onload = () => {
      if (this.isDestroyed) return;

      this.createContainerAndIframe();
      this.injectBaseStyles();
      this.setupMessageListener();
      this.setupResponsiveListener();
      this.setupUrlListener();
      this.hide(); // Start hidden
    };

    if (['interactive', 'complete'].includes(document.readyState)) {
      onload();
    } else {
      this.loadListener = onload;
      window.addEventListener('load', this.loadListener, false);
    }
  }

  private createContainerAndIframe(): void {
    // Check if container already exists to prevent duplicates
    const existingContainer = document.querySelector('.tronic-iframe-container');
    if (existingContainer) {
      // Reuse existing container
      this.container = existingContainer as HTMLDivElement;

      // Check if iframe already exists in the container
      const existingIframe = this.container.querySelector('iframe');
      if (existingIframe) {
        this.iframe = existingIframe;
        // Apply base styles to existing container
        this.applyContainerStyles();
        return;
      }
    } else {
      // Create new container only if none exists
      this.container = document.createElement('div');
      this.container.classList.add('tronic-iframe-container');
      document.body.appendChild(this.container);
    }

    // Create iframe with preload optimizations
    const endpointUrl = createURL(this.endpoint);
    this.iframe = document.createElement('iframe');
    this.iframe.src = `${endpointUrl.href}?params=${encodeURIComponent(this.parameters)}`;
    this.iframe.allow = 'publickey-credentials-get';

    // Preload optimizations for faster loading
    this.iframe.referrerPolicy = 'same-origin'; // Optimize referrer for faster load

    // Add preconnect hint for faster DNS/TLS setup
    this.addPreconnectHint(endpointUrl.origin);

    this.container.appendChild(this.iframe);

    // Apply base styles
    this.applyContainerStyles();
  }

  /**
   * Add preconnect hint to speed up initial connection to iframe origin
   */
  private addPreconnectHint(origin: string): void {
    const existingPreconnect = document.querySelector(`link[rel="preconnect"][href="${origin}"]`);
    if (existingPreconnect) return;

    const preconnectLink = document.createElement('link');
    preconnectLink.rel = 'preconnect';
    preconnectLink.href = origin;
    preconnectLink.crossOrigin = 'anonymous';
    document.head.appendChild(preconnectLink);
  }

  private injectBaseStyles(): void {
    const baseStyles = {
      '@media print': {
        '.tronic-iframe-container': {
          display: 'none',
        },
      },
      // Anti-bleeding styles for iframe content
      '.tronic-iframe-container iframe': {
        // Force iframe to respect rounded corners in all browsers
        mask: 'radial-gradient(circle at center, white 100%, transparent 100%)',
        WebkitMask: 'radial-gradient(circle at center, white 100%, transparent 100%)',
      },
      // Ensure any nested content respects the clipping
      '.tronic-iframe-container *': {
        borderRadius: 'inherit',
        overflow: 'hidden',
      },
    };

    injectCSS(styleObjectToCSS(baseStyles), 'tronic-base-styles');
  }

  private setupMessageListener(): void {
    if (this.messageListener) {
      // Remove existing listener if any
      window.removeEventListener('message', this.messageListener);
    }

    this.messageListener = (event: MessageEvent) => {
      if (this.isDestroyed) return;

      if (event.origin === new URL(this.endpoint).origin) {
        if (event.data && event.data.msgType && this.messageHandlers.size) {
          event.data.response = event.data.response ?? {};
          for (const handler of this.messageHandlers.values()) {
            handler(event);
          }
        }
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  private setupResponsiveListener(): void {
    if (this.resizeListener) {
      // Remove existing listener if any
      window.removeEventListener('resize', this.resizeListener);
    }

    this.resizeListener = () => {
      if (this.isDestroyed) return;

      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 600;

      // Re-apply styles if mobile state changed and we're visible
      if (wasMobile !== this.isMobile && this.isVisible) {
        this.applyContainerStyles();
      }
    };

    // Initial check
    this.isMobile = window.innerWidth <= 600;
    window.addEventListener('resize', this.resizeListener);
  }

  private setupUrlListener(): void {
    if (!this.config.enableUrlListening) return;

    // Store current URL
    this.lastKnownUrl = window.location.href;

    // Create URL change listener
    this.urlListener = (event: PopStateEvent) => {
      const currentUrl = window.location.href;

      // Only process if URL actually changed
      if (currentUrl !== this.lastKnownUrl) {
        this.lastKnownUrl = currentUrl;
        this.handleUrlChange(currentUrl);
      }
    };

    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', this.urlListener);

    // Also listen for pushstate/replacestate using a wrapper approach
    this.setupHistoryAPIListener();
  }

  private setupHistoryAPIListener(): void {
    // Store original methods for cleanup
    if (!this.originalPushState) {
      this.originalPushState = window.history.pushState;
    }
    if (!this.originalReplaceState) {
      this.originalReplaceState = window.history.replaceState;
    }

    const handleHistoryChange = () => {
      setTimeout(() => {
        if (this.isDestroyed) return;

        const currentUrl = window.location.href;
        if (currentUrl !== this.lastKnownUrl) {
          this.lastKnownUrl = currentUrl;
          this.handleUrlChange(currentUrl);
        }
      }, 0);
    };

    window.history.pushState = (...args: Parameters<typeof History.prototype.pushState>) => {
      if (this.originalPushState) {
        this.originalPushState.apply(window.history, args);
      }
      handleHistoryChange();
    };

    window.history.replaceState = (...args: Parameters<typeof History.prototype.replaceState>) => {
      if (this.originalReplaceState) {
        this.originalReplaceState.apply(window.history, args);
      }
      handleHistoryChange();
    };
  }

  private handleUrlChange(url: string): void {
    try {
      const route = this.config.urlMapper ? this.config.urlMapper(url) : new URL(url).pathname;

      if (route) {
        this.navigateToRoute(route);
      }
    } catch (error) {
      console.warn('⚠️ [IframeController] Failed to handle URL change:', error);
    }
  }

  /**
   * Navigate to a specific route within the iframe
   * @param route - The route to navigate to
   */
  public navigateToRoute(route: string): void {
    if (!this.iframe || !this.iframe.contentWindow) {
      console.warn('⚠️ [IframeController] Cannot navigate: iframe not ready');
      return;
    }

    try {
      // Create JSON-RPC request payload
      const payload = {
        id: Math.random().toString(36).substr(2, 9),
        jsonrpc: '2.0',
        method: TronicPayloadMethod.Navigate,
        params: [route],
      };

      // Send with correct msgType format including parameters suffix
      const message = {
        msgType: `TRONIC_HANDLE_REQUEST-${this.parameters}`,
        payload,
      };

      this.iframe.contentWindow.postMessage(message, new URL(this.endpoint).origin);
    } catch (error) {
      console.warn('⚠️ [IframeController] Navigation failed:', error);
    }
  }

  /**
   * Cleanup URL listeners
   */
  private cleanupUrlListener(): void {
    if (this.urlListener) {
      window.removeEventListener('popstate', this.urlListener);
      this.urlListener = null;
    }
  }

  private applyContainerStyles(): void {
    if (!this.container) return;

    // Base styles (always applied)
    applyStylesToElement(this.container, IFRAME_STYLES.containerBase);

    // State-based styles
    if (!this.isVisible) {
      applyStylesToElement(this.container, IFRAME_STYLES.containerHidden);
      return;
    }

    // Visible state
    applyStylesToElement(this.container, IFRAME_STYLES.containerVisible);

    // Size and position based on mobile/desktop
    if (this.isMobile) {
      const { classList } = this.container;
      if (classList.contains('with-launcher-top-right')) {
        applyStylesToElement(this.container, IFRAME_STYLES.containerMobileTopRight);
      } else if (classList.contains('with-launcher-bottom-right')) {
        applyStylesToElement(this.container, IFRAME_STYLES.containerMobileBottomRight);
      } else if (classList.contains('with-launcher-bottom-left')) {
        applyStylesToElement(this.container, IFRAME_STYLES.containerMobileBottomLeft);
      } else {
        applyStylesToElement(this.container, IFRAME_STYLES.containerMobile);
      }
    } else {
      // Desktop: LauncherController handles all positioning dynamically
      // We only apply fallback styles for cases without launcher
      const hasLauncherClass =
        this.container.classList.contains('with-launcher-top-right') ||
        this.container.classList.contains('with-launcher-bottom-right') ||
        this.container.classList.contains('with-launcher-bottom-left');

      if (!hasLauncherClass) {
        // No launcher - use default positioning
        applyStylesToElement(this.container, IFRAME_STYLES.containerNormal);
      }
      // If launcher class exists, LauncherController handles all positioning dynamically
    }

    // Apply iframe styles
    if (this.iframe) {
      applyStylesToElement(this.iframe, IFRAME_STYLES.iframe);
    }
  }

  private getLauncherPosition(): string | null {
    if (!this.container) return null;

    // Check for launcher position classes
    const { classList } = this.container;
    if (classList.contains('with-launcher-top-right')) return 'top-right';
    if (classList.contains('with-launcher-bottom-right')) return 'bottom-right';
    if (classList.contains('with-launcher-bottom-left')) return 'bottom-left';

    return null;
  }

  private formatLauncherPosition(position: string): string {
    // Convert 'top-right' -> 'TopRight', 'bottom-left' -> 'BottomLeft', etc.
    return position
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }

  protected async _post(data: any) {
    if (this.iframe && this.iframe.contentWindow) {
      this.iframe.contentWindow.postMessage(data, new URL(this.endpoint).origin);
    } else {
      throw createModalNotReadyError();
    }
  }

  // Public API - Single source of truth methods

  /**
   * Show the iframe overlay
   */
  public show(): void {
    if (this.isVisible) return;

    this.isVisible = true;
    this.applyContainerStyles();
  }

  /**
   * Hide the iframe overlay
   */
  public hide(): void {
    if (!this.isVisible) return;

    this.isVisible = false;
    this.applyContainerStyles();
  }

  /**
   * Toggle iframe visibility
   */
  public toggleVisibility(): void {
    if (this.isVisible) {
      this.hide();
    } else {
      this.show();
    }
  }

  // Getters for state inspection

  /**
   * Check if iframe is currently visible
   */
  public isOverlayVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Check if currently in mobile mode
   */
  public isMobileMode(): boolean {
    return this.isMobile;
  }

  // Legacy method aliases for backward compatibility

  /**
   * @deprecated Use show() instead
   */
  public showOverlay(): void {
    this.show();
  }

  /**
   * @deprecated Use hide() instead
   */
  public hideOverlay(): void {
    this.hide();
  }

  /**
   * Cleanup all resources and event listeners
   * Should be called when the component is no longer needed
   */
  public destroy(): void {
    if (this.isDestroyed) {
      console.warn('⚠️ [IframeController] Instance already destroyed');
      return;
    }

    this.isDestroyed = true;

    // Cleanup event listeners
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }

    if (this.loadListener) {
      window.removeEventListener('load', this.loadListener);
      this.loadListener = null;
    }

    // Cleanup URL listener
    this.cleanupUrlListener();

    // Restore original history methods
    if (this.originalPushState) {
      window.history.pushState = this.originalPushState;
      this.originalPushState = null;
    }

    if (this.originalReplaceState) {
      window.history.replaceState = this.originalReplaceState;
      this.originalReplaceState = null;
    }

    // Remove DOM elements
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }

    // Clear references
    this.iframe = null;
    this.container = null;
    this.urlListener = null;
    this.lastKnownUrl = '';

    // Clear message handlers from parent class
    if (this.messageHandlers) {
      this.messageHandlers.clear();
    }

    console.log('✅ [IframeController] Successfully destroyed and cleaned up resources');
  }
}
